import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Alert } from '@/design-system/alert';
import { Button } from '@/design-system/button';
import { TableData, type TableDataColumn } from '@/design-system/table-data';

import { warehouseApi } from '../../api/warehouse.api';
import { GoodsReceiptTable } from '../../components/GoodsReceiptTable';
import {
  PoSourceKVs,
  PoTypeTag,
  TransferCancelHint,
} from '../../components/orderType';
import {
  HistorySection,
  purchaseOrderHistory,
} from '../../components/history';
import {
  codeCls,
  DetailHeader,
  DetailSkeleton,
  KV,
  LoadError,
  linkCls,
  MappedBadge,
  metaCls,
  nameCls,
  numCls,
  ProgressCell,
  Section,
} from '../../components/ui';
import { usePurchaseOrderActions } from '../../components/usePurchaseOrderActions';
import { fmtDateTime, fmtQty } from '../../format';
import { useLoad } from '../../hooks/useLoad';
import { PO_STATUS, RECEIPT_STATUS } from '../../status';
import type { PurchaseOrderLine } from '../../types';
import { useWarehouse } from '../../WarehouseContext';
import { WAREHOUSE_PATHS } from '../../paths';

const COLUMNS: TableDataColumn<PurchaseOrderLine>[] = [
  {
    key: 'sku',
    title: 'SKU',
    render: (_v, l) => <span className={codeCls}>{l.sku}</span>,
  },
  {
    key: 'itemName',
    title: 'Product name',
    render: (_v, l) => <span className={nameCls}>{l.itemName}</span>,
  },
  {
    key: 'qty',
    title: 'Expected qty',
    align: 'right',
    render: (_v, l) => <span className={numCls}>{fmtQty(l.qty)}</span>,
  },
  {
    key: 'qtyReceived',
    title: 'Received qty',
    align: 'right',
    render: (_v, l) => <span className={numCls}>{fmtQty(l.qtyReceived)}</span>,
  },
  {
    key: 'uomName',
    title: 'UoM',
    render: (_v, l) => <span className={metaCls}>{l.uomName}</span>,
  },
];

const InboundRequestDetailPage: React.FC = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { refreshToken } = useWarehouse();
  const detail = useLoad(
    (signal) => warehouseApi.getInboundRequest(id, { signal }),
    [id, refreshToken]
  );
  const actions = usePurchaseOrderActions(detail.reload, { as: 'inbound' });

  if (detail.loading && !detail.data) return <DetailSkeleton />;
  if (detail.error || !detail.data) {
    return (
      <div className="space-y-5 p-6">
        <DetailHeader
          backTo={WAREHOUSE_PATHS.inboundRequest}
          backLabel="Inbound request"
          title={id}
        />
        <LoadError
          message={detail.error?.message ?? 'Inbound request tidak ditemukan'}
          onRetry={detail.reload}
        />
      </div>
    );
  }
  const { order: x, lines, goodsReceipts } = detail.data;

  return (
    <div className="space-y-5 p-6">
      <DetailHeader
        backTo={WAREHOUSE_PATHS.inboundRequest}
        backLabel="Inbound request"
        meta={`Confirmed ${fmtDateTime(x.confirmedAt)} · ${x.supplierName}`}
        title={x.code}
        badges={
          <>
            {x.status === 'CANCELLED' ? (
              <MappedBadge map={PO_STATUS} value={x.status} />
            ) : (
              <MappedBadge map={RECEIPT_STATUS} value={x.receiptStatus} />
            )}
            <PoTypeTag type={x.type} />
          </>
        }
        actions={
          <>
            {actions.canCancel(x) && (
              <Button
                tone="destructive"
                style="stroke"
                data-testid="inbound-cancel"
                onClick={() => actions.requestCancel(x)}
                leftIcon={<i className="ri-close-circle-line text-[14px]" />}
              >
                Batalkan inbound request
              </Button>
            )}
            {x.receiptStatus !== 'FINISHED' && x.status === 'CONFIRMED' && (
              <Button
                onClick={() =>
                  navigate(`${WAREHOUSE_PATHS.goodsReceipt}/new?po=${x.id}`)
                }
                leftIcon={<i className="ri-add-line text-[14px]" />}
              >
                Buat goods receipt
              </Button>
            )}
          </>
        }
      />
      {x.status === 'CANCELLED' && (
        <Alert
          status="warning"
          appearance="stroke"
          size="lg"
          title="Inbound request dibatalkan"
          data-testid="inbound-cancelled"
        >
          {x.cancellationReason || '—'} · {fmtDateTime(x.cancelledAt)}
          {x.cancelledBy ? ` · ${x.cancelledBy}` : ''}
        </Alert>
      )}
      <TransferCancelHint po={x} />
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div className="flex flex-col gap-5 xl:col-span-2">
          <Section title="Inbound lines">
            <TableData size="sm" data={lines} columns={COLUMNS} />
          </Section>
          <Section title="Goods receipt">
            <GoodsReceiptTable
              rows={goodsReceipts}
              emptyTitle="Belum ada goods receipt untuk inbound ini"
            />
          </Section>
        </div>
        <div className="flex flex-col gap-5">
          <Section title="Info">
            <KV
              label="Client"
              value={x.client?.name ?? `Client #${x.clientID}`}
            />
            <KV
              label="Purchase order"
              value={
                <Link
                  to={`${WAREHOUSE_PATHS.purchaseOrder}/${x.id}`}
                  className={linkCls}
                >
                  {x.code}
                </Link>
              }
            />
            <PoSourceKVs po={x} />
            <KV label="Hub" value={x.hub?.name ?? `Hub #${x.hubID}`} />
            <KV label="Est. arrival" value={fmtDateTime(x.estArrivalAt)} />
            <KV
              label="Progress"
              value={
                <ProgressCell
                  received={x.totalReceived}
                  expected={x.totalQty}
                  format={fmtQty}
                />
              }
            />
            {x.notes && <KV label="Catatan" value={x.notes} />}
          </Section>
          <HistorySection entries={purchaseOrderHistory(x, goodsReceipts)} />
        </div>
      </div>
      {actions.dialogs}
    </div>
  );
};

export default InboundRequestDetailPage;
